import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@miyuki/ui';

import type { MessageKey } from '@/i18n/es';
import { t } from '@/lib/i18n';

/**
 * Guía rápida de uso.
 *
 * Existe porque la regla de gestos cambió con la feature 002 —un dedo pinta,
 * dos dedos navegan— y no estaba escrita en ninguna parte: la usuaria la
 * descubría probando y mientras tanto creía que el desplazamiento estaba roto.
 *
 * Cada tema nombra el gesto o el control concreto, no la idea general: «arrastra
 * con dos dedos» sirve; «puedes navegar el lienzo» no. Y no menciona el doble
 * toque, derogado en la 002.
 */
export interface QuickGuideSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

const TOPICS: readonly { title: MessageKey; body: MessageKey }[] = [
  { title: 'guidePanTitle', body: 'guidePanBody' },
  { title: 'guideZoomTitle', body: 'guideZoomBody' },
  { title: 'guideSaveTitle', body: 'guideSaveBody' },
  { title: 'guideNewDesignTitle', body: 'guideNewDesignBody' },
  { title: 'guidePaletteTitle', body: 'guidePaletteBody' },
  { title: 'guideRowsTitle', body: 'guideRowsBody' },
  { title: 'guidePatternTypesTitle', body: 'guidePatternTypesBody' },
];

export function QuickGuideSheet({ open, onClose }: QuickGuideSheetProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {/* Si el contenido no cabe, se recorre: no se recorta (RF-33). */}
      <SheetContent side="bottom" className="max-h-[85svh] overflow-y-auto pb-8">
        <SheetHeader>
          <SheetTitle>{t('quickGuide')}</SheetTitle>
          <SheetDescription className="sr-only">{t('quickGuide')}</SheetDescription>
        </SheetHeader>

        <dl className="flex flex-col gap-4 px-4 pb-2">
          {TOPICS.map((topic) => (
            <div key={topic.title} className="flex flex-col gap-1">
              <dt className="text-sm font-medium">{t(topic.title)}</dt>
              <dd className="text-muted-foreground text-sm">{t(topic.body)}</dd>
            </div>
          ))}
        </dl>
      </SheetContent>
    </Sheet>
  );
}
